import { useEffect, useRef } from "react";

interface ContactFormProps {
  /** "dark" renders on a dark/primary background — form is an iframe so this only affects the wrapper */
  variant?: "dark" | "light";
}

const FORM_ID = "mSCbnRekbSgbrl494r7r";
const SCRIPT_SRC = "https://link.msgsndr.com/js/form_embed.js";

/**
 * Caribbean Breeze HVAC contact form — LeadConnector (GoHighLevel) embed.
 * Submissions go directly to the dealer's CRM.
 *
 * The iframe is created imperatively (not via JSX) because GHL's form_embed.js
 * physically moves the iframe node into its own wrapper element after it runs.
 * If React owns the iframe via JSX, it crashes with a removeChild NotFoundError
 * when it tries to unmount a node that is no longer where it expects it to be.
 * Creating it imperatively means React never tracks the iframe, so cleanup is
 * safe regardless of where GHL moved it.
 */
export function ContactForm({ variant = "light" }: ContactFormProps) {
  const mountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // Build the iframe imperatively so React never manages this DOM node.
    const iframe = document.createElement("iframe");
    iframe.src = `https://api.leadconnectorhq.com/widget/form/${FORM_ID}`;
    iframe.style.cssText = "width:100%;height:847px;border:none;border-radius:8px";
    iframe.id = `inline-${FORM_ID}`;
    iframe.setAttribute("data-layout", "{'id':'INLINE'}");
    iframe.setAttribute("data-trigger-type", "alwaysShow");
    iframe.setAttribute("data-trigger-value", "");
    iframe.setAttribute("data-activation-type", "alwaysActivated");
    iframe.setAttribute("data-activation-value", "");
    iframe.setAttribute("data-deactivation-type", "neverDeactivate");
    iframe.setAttribute("data-deactivation-value", "");
    iframe.setAttribute("data-form-name", "Contact form");
    iframe.setAttribute("data-height", "847");
    iframe.setAttribute("data-layout-iframe-id", `inline-${FORM_ID}`);
    iframe.setAttribute("data-form-id", FORM_ID);
    iframe.title = "Contact form";
    mount.appendChild(iframe);

    // form_embed.js scans for iframes present at load time. In an SPA an
    // already-loaded script won't re-initialize iframes mounted later, so
    // remove any prior copy and re-append to force a fresh DOM scan.
    const prior = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (prior) prior.remove();
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Remove from wherever GHL may have moved it — safe regardless of position.
      iframe.parentNode?.removeChild(iframe);
    };
  }, []);

  return (
    /*
     * Outer div: caps height on mobile so the 847 px iframe doesn't dominate
     * the viewport. On sm+ (≥640 px) the constraint is removed.
     */
    <div
      className={[
        "max-h-[72vh] overflow-y-auto sm:max-h-none sm:overflow-visible rounded-lg",
        variant === "dark" ? "bg-white" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-testid="contact-form"
    >
      {/* GHL mounts into this div — keep it empty; React must not render children here */}
      <div ref={mountRef} />
    </div>
  );
}

export default ContactForm;
